<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <!-- On mobile, it's full width, allowing the sidebar to elegantly overlap it. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Process Checklist</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Last updated: {{ currentDate }}</p>
          </div>
          <button 
            @click="refreshData"
            class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] dark:focus:ring-offset-gray-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" :class="{'animate-spin': isRefreshing}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>

        <!-- Summary Stats Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          <!-- Total Applicants -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-black-600 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Applicants</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ summary.totalStaff }}</p>
              </div>
              <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">All recorded applicants</p>
          </div>

          <!-- Completed Checklists -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-black-600 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Completed Checklists</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ summary.completedChecklist }}</p>
              </div>
              <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Successfully completed all processes</p>
          </div>

          <!-- Incomplete Checklists -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-black-600 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Incomplete Checklists</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ summary.uncompletedChecklist }}</p>
              </div>
              <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Pending process completion</p>
          </div>

          <!-- Total Processes -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-black-600 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Process Steps</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ summary.totalProcesses }}</p>
              </div>
              <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-4">Required steps per applicant</p>
          </div>
        </div>

        <!-- Filter Section -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-black-600 dark:border-gray-700 p-6 mb-8">
          <div class="flex justify-between items-center mb-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Filter Applicants</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">Narrow down the list using the criteria below</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <!-- Position -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Position</label>
              <select v-model="filters.position" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Positions</option>
                <option v-for="pos in positions" :key="pos.id" :value="pos.position_name">{{ pos.position_name }}</option>
              </select>
            </div>

            <!-- Period -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Period</label>
              <select v-model="filters.period" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Terms</option>
                <option v-for="p in periods" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>

            <!-- Month -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Month</label>
              <select v-model="selectedMonth" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Months</option>
                <option v-for="m in months" :key="m.value" :value="m.value">{{ m.name }}</option>
              </select>
            </div>

            <!-- Year -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Year</label>
              <select v-model="selectedYear" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition">
                <option value="">All Years</option>
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>

            <!-- Search -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wide">Search</label>
              <div class="relative">
                <input
                  v-model="filters.search"
                  type="text"
                  placeholder="Name or position..."
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-[#74bd54] focus:border-transparent outline-none transition"
                />
                <svg class="w-4 h-4 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Filter Actions -->
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-6 pt-4 border border-black-600 dark:border-gray-700 gap-4">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              Showing <span class="font-semibold text-gray-900 dark:text-white">{{ filteredApplicants.length }}</span> results
            </div>
            <div class="flex space-x-3 w-full sm:w-auto">
              <button @click="resetFilters" class="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">
                Reset
              </button>
              <button @click="applyFilters" class="flex-1 sm:flex-none px-6 py-2 rounded-lg bg-[#74bd54] text-white shadow-sm hover:bg-[#65a848] transition font-medium text-sm">
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        <!-- Applicants Table -->
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-black-600 dark:border-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completion</th>
                  <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                <tr v-for="applicant in filteredApplicants" :key="applicant.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ applicant.name }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ applicant.position }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getStatusColor(applicant.status)" class="px-2.5 py-1 rounded-full text-xs font-semibold capitalize">
                      {{ applicant.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ applicant.period }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{{ formatDate(applicant.date) }}</td>
                  
                  <!-- COMPLETION PROGRESS BAR -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="w-32">
                      <div class="flex justify-between items-center mb-1">
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                          {{ applicant.completed_process_count }}/{{ applicant.total_processes }}
                        </span>
                        <span class="text-xs font-bold" :class="getProgressColor(applicant)">
                          {{ getCompletionPercentage(applicant) }}%
                        </span>
                      </div>
                      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div 
                          class="h-2 rounded-full transition-all duration-500 ease-out"
                          :class="getProgressBgColor(applicant)"
                          :style="{ width: `${getCompletionPercentage(applicant)}%` }"
                        ></div>
                      </div>
                    </div>
                  </td>

                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      v-if="hasPermission('checklist', 'view_checklist')"
                      @click="openCheckListModal(applicant)" 
                      class="inline-flex items-center justify-center p-2 rounded-lg text-[#035a76] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-[#74bd54] dark:hover:text-green-400 transition-colors"
                      title="View Checklist"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </td>
                </tr>
                
                <!-- Empty State -->
                <tr v-if="filteredApplicants.length === 0">
                  <td colspan="7" class="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p class="text-sm font-medium">No applicants found matching your filters.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Modals -->
        <Teleport to="body">
          <ChecklistModal
            v-if="selectedApplicant"
            :applicant="selectedApplicant"
            @close="selectedApplicant = null"
            @save="handleChecklistSave"
          />           
        </Teleport>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api';
import HrSidebar from '@/components/hr/HrSidebar.vue';
import HrTopbar from '@/components/hr/HrTopbar.vue';
import ChecklistModal from '@/components/hr/ChecklistModal.vue';
import { usePermissions } from '@/composables/usePermissions';

const { hasPermission } = usePermissions();

const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);
const isRefreshing = ref(false);
const currentPageTitle = ref('Process Checklist');
const selectedApplicant = ref(null);

const positions = ref([]);
const applicants = ref([]);
const agencies = ref([]);
const staffMembers = ref([]);

const filters = ref({
  status: '',
  position: '',
  period: '',
  search: ''
});

const periods = ref(['First Term', 'Second Term', 'Third Term']);
const selectedMonth = ref('');
const selectedYear = ref('');

const months = [
  { value: '01', name: 'January' }, { value: '02', name: 'February' },
  { value: '03', name: 'March' }, { value: '04', name: 'April' },
  { value: '05', name: 'May' }, { value: '06', name: 'June' },
  { value: '07', name: 'July' }, { value: '08', name: 'August' },
  { value: '09', name: 'September' }, { value: '10', name: 'October' },
  { value: '11', name: 'November' }, { value: '12', name: 'December' }
];

const years = computed(() => {
  const unique = new Set();
  applicants.value.forEach(app => {
    if (app.date) {
      const year = app.date.split('-')[0];
      unique.add(year);
    }
  });
  return Array.from(unique).sort().reverse();
});

const currentDate = computed(() => {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
});

// --- Helpers for UI ---
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusColor = (status) => {
  const map = {
    'Hired': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'Kept': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    'Rejected': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'Processing': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
  };
  return map[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getCompletionPercentage = (applicant) => {
  if (!applicant.total_processes || applicant.total_processes === 0) return 0;
  return Math.min(100, Math.round((applicant.completed_process_count / applicant.total_processes) * 100));
};

const getProgressColor = (applicant) => {
  const pct = getCompletionPercentage(applicant);
  if (pct === 100) return 'text-green-600 dark:text-green-400';
  if (pct >= 50) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const getProgressBgColor = (applicant) => {
  const pct = getCompletionPercentage(applicant);
  if (pct === 100) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

// --- Computed ---
const filteredApplicants = computed(() => {
  return applicants.value.filter(applicant => {
    const matchMonth = !selectedMonth.value || applicant.date?.split('-')[1] === selectedMonth.value;
    const matchYear = !selectedYear.value || applicant.date?.split('-')[0] === selectedYear.value;
    const matchPeriod = !filters.value.period || applicant.period === filters.value.period;
    const matchPosition = !filters.value.position || applicant.position === filters.value.position;
    const matchSearch = !filters.value.search || applicant.name.toLowerCase().includes(filters.value.search.toLowerCase());

    return matchMonth && matchYear && matchPeriod && matchPosition && matchSearch;
  });
});

const summary = computed(() => {
  const totalStaff = applicants.value.length;
  const hiredApplicants = applicants.value.filter(app => app.status === 'Hired');
  
  let completedChecklist = 0;
  let uncompletedChecklist = 0;

  hiredApplicants.forEach(app => {
    const completed = app.completed_process_count ?? 0; 
    const totalProcesses = app.total_processes ?? 0;
    if (completed >= totalProcesses && totalProcesses > 0) {
      completedChecklist++;
    } else {
      uncompletedChecklist++;
    }
  });

  const totalProcesses = applicants.value[0]?.total_processes ?? 0;

  return { totalStaff, totalHired: hiredApplicants.length, completedChecklist, uncompletedChecklist, totalProcesses };
});

// --- Actions ---
const resetFilters = () => {
  filters.value = { status: '', position: '', period: '', search: '' };
  selectedMonth.value = '';
  selectedYear.value = '';
};

const applyFilters = () => {
  // Reactive computed property handles this automatically, 
  // but kept for explicit button action if needed in the future.
};

const refreshData = () => {
  isRefreshing.value = true;
  setTimeout(() => {
    fetchApplicants();
    isRefreshing.value = false;
  }, 800);
};

const openCheckListModal = (applicant) => {
  selectedApplicant.value = applicant;
};

const handleChecklistSave = () => {
  fetchApplicants();
  selectedApplicant.value = null;
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

// --- API Calls ---
const fetchPositions = async () => {
  try {
    const response = await api.get("Positions.php");
    positions.value = response.data.map(position => ({
      id: position.id,
      position_name: position.title,
    }));
  } catch (error) {
    console.error("Error fetching Positions:", error);
  }
};

const fetchApplicants = async () => {
  try {
    const response = await api.get("CheckListStaff.php");
    applicants.value = response.data.applicants.map(applicant => ({
      id: applicant.id,
      name: applicant.full_name,
      position: applicant.position_title,
      status: applicant.applicant_status,
      period: applicant.period,
      date: applicant.created_at,
      completed_process_count: Number(applicant.completed_process_count),
      total_processes: Number(applicant.total_processes)
    }));
  } catch (error) {
    console.error("Error fetching Applicants:", error);
  }
};

const fetchAgencies = async () => {
  try {
    const response = await api.get("Agencies.php");
    agencies.value = response.data;
  } catch (error) {
    console.error("Error fetching Agencies:", error);
  }
};

const fetchInward = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    staffMembers.value = response.data.data;
  } catch (error) {
    console.error('Error fetching staff members:', error);
  }
};

onMounted(() => {
  fetchPositions();
  fetchApplicants();
  fetchAgencies();
  fetchInward();
  
  // Initialize dark mode from localStorage
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
});
</script>